/**

 * Reimbursement Permission Constants and Utilities

 *

 * UI gating mirrors backend `assertCanActAtStage()` in

 * `backend-spec/reimbursements/reimbursement.controller.js`:

 * CASL abilities only — no roleId checks.

 */



export const REIMBURSEMENT_PERMISSIONS = {

  APPROVE_LEVEL1: 'reimbursement_level1',

  APPROVE_LEVEL2: 'reimbursement_level2',

  APPROVE_FINAL: 'reimbursement_final',

  VIEW_ALL: 'reimbursement_all',

  VIEW_APPROVALS: 'reimbursement_approvals',

  APPLY_ON_BEHALF: 'apply_on_behalf_reimbursement',

  APPLY_ON_BEHALF_ALL: 'apply_on_behalf_reimbursement_all',

}



export const PERMISSION_ACTIONS = {

  READ: 'read',

  CREATE: 'create',

  APPROVE: 'approve',

  APPLY_ON_BEHALF: 'apply_on_behalf',

}



export const REIMBURSEMENT_STATUSES = {

  PENDING_L1: 'Pending Level 1',

  PENDING_L2: 'Pending Level 2',

  PENDING_FINAL: 'Pending Final',

  APPROVED: 'Approved',

  REJECTED: 'Rejected',

}



/** Statuses where a claim is still in the approval pipeline (not terminal). */

export const PENDING_REIMBURSEMENT_STATUSES = [

  REIMBURSEMENT_STATUSES.PENDING_L1,

  REIMBURSEMENT_STATUSES.PENDING_L2,

  REIMBURSEMENT_STATUSES.PENDING_FINAL,

]



/** Status that each CASL approval subject acts on. */

export const STATUS_FOR_APPROVAL_PERMISSION = {

  [REIMBURSEMENT_PERMISSIONS.APPROVE_LEVEL1]: REIMBURSEMENT_STATUSES.PENDING_L1,

  [REIMBURSEMENT_PERMISSIONS.APPROVE_LEVEL2]: REIMBURSEMENT_STATUSES.PENDING_L2,

  [REIMBURSEMENT_PERMISSIONS.APPROVE_FINAL]: REIMBURSEMENT_STATUSES.PENDING_FINAL,

}



/** Pending status → required approve permission subject. */

export const PERMISSION_BY_STATUS = {

  [REIMBURSEMENT_STATUSES.PENDING_L1]: REIMBURSEMENT_PERMISSIONS.APPROVE_LEVEL1,

  [REIMBURSEMENT_STATUSES.PENDING_L2]: REIMBURSEMENT_PERMISSIONS.APPROVE_LEVEL2,

  [REIMBURSEMENT_STATUSES.PENDING_FINAL]: REIMBURSEMENT_PERMISSIONS.APPROVE_FINAL,

}



/** Next status after approval at each stage. */

export const NEXT_STATUS_AFTER_APPROVAL = {

  [REIMBURSEMENT_STATUSES.PENDING_L1]: REIMBURSEMENT_STATUSES.PENDING_L2,

  [REIMBURSEMENT_STATUSES.PENDING_L2]: REIMBURSEMENT_STATUSES.PENDING_FINAL,

  [REIMBURSEMENT_STATUSES.PENDING_FINAL]: REIMBURSEMENT_STATUSES.APPROVED,

}

/**
 * Human-readable role/step names for each stored approval status.
 * DB/API keep "Pending Level 1" etc.; UI, filters, pills, and emails use these.
 *
 * Defaults match the live queues (L1 → HR / Role 10, L2 → Finance / Role 11,
 * Final → Manager). Set a name to '' to fall back to "Level 1" / "Level 2" / "Final".
 */
export const REIMBURSEMENT_STAGE_ROLE_NAMES = {
  [REIMBURSEMENT_STATUSES.PENDING_L1]: 'HR',
  [REIMBURSEMENT_STATUSES.PENDING_L2]: 'Finance',
  [REIMBURSEMENT_STATUSES.PENDING_FINAL]: 'Manager',
}

const FALLBACK_STAGE_NAMES = {
  [REIMBURSEMENT_STATUSES.PENDING_L1]: 'Level 1',
  [REIMBURSEMENT_STATUSES.PENDING_L2]: 'Level 2',
  [REIMBURSEMENT_STATUSES.PENDING_FINAL]: 'Final',
}

function pendingStatusFromStageToken(value) {
  if (value == null || value === '') return ''
  const raw = String(value).trim()
  if (Object.values(REIMBURSEMENT_STATUSES).includes(raw)) return raw
  const lower = raw.toLowerCase()
  if (lower === 'level 1' || lower === 'l1' || /level\s*1/.test(lower)) {
    return REIMBURSEMENT_STATUSES.PENDING_L1
  }
  if (lower === 'level 2' || lower === 'l2' || /level\s*2/.test(lower)) {
    return REIMBURSEMENT_STATUSES.PENDING_L2
  }
  if (lower === 'final' || lower === 'manager' || /pending\s+final/.test(lower)) {
    return REIMBURSEMENT_STATUSES.PENDING_FINAL
  }
  if (lower === 'hr' || /pending\s+level\s*1/.test(lower)) {
    return REIMBURSEMENT_STATUSES.PENDING_L1
  }
  if (lower === 'finance' || /pending\s+level\s*2/.test(lower)) {
    return REIMBURSEMENT_STATUSES.PENDING_L2
  }
  return raw
}

/** Role/step name for a stored status or "Level 1" token, e.g. "HR". */
export function getReimbursementStageName(statusOrToken) {
  const pending = pendingStatusFromStageToken(statusOrToken)
  const configured = REIMBURSEMENT_STAGE_ROLE_NAMES[pending]
  if (configured != null && String(configured).trim() !== '') {
    return String(configured).trim()
  }
  return FALLBACK_STAGE_NAMES[pending] || null
}

/**
 * @param {string} statusOrToken stored status or stage token
 * @param {'pending'|'approved'|'rejected'} outcome
 * @returns {string} e.g. "HR Pending", "Finance Approved", "HR Rejected"
 */
export function formatReimbursementStageOutcome(statusOrToken, outcome = 'pending') {
  const name = getReimbursementStageName(statusOrToken)
  const suffix = outcome === 'approved'
    ? 'Approved'
    : outcome === 'rejected'
      ? 'Rejected'
      : 'Pending'
  if (!name) return suffix
  return `${name} ${suffix}`
}

/** Status pill / filter / email Status value. Rejected rows become "HR Rejected" when stage is known. */
export function formatReimbursementStatusLabel(statusOrRow) {
  const row = statusOrRow && typeof statusOrRow === 'object' ? statusOrRow : null
  const status = row
    ? normalizeReimbursementStatus(row)
    : pendingStatusFromStageToken(statusOrRow) || String(statusOrRow || '').trim()

  if (status === REIMBURSEMENT_STATUSES.APPROVED) return 'Approved'
  if (status === REIMBURSEMENT_STATUSES.REJECTED) {
    const rejectedStage = row && (row.rejected_stage || row.rejectedStage)
    if (rejectedStage) return formatReimbursementStageOutcome(rejectedStage, 'rejected')
    return 'Rejected'
  }
  if (PENDING_REIMBURSEMENT_STATUSES.includes(status)) {
    return formatReimbursementStageOutcome(status, 'pending')
  }
  const stageName = getReimbursementStageName(status)
  if (stageName && status !== stageName) {
    return formatReimbursementStageOutcome(status, 'pending')
  }
  return status || '-'
}

/** "HR → Finance → Manager" (or Level 1 → … if a name is blank). */
export function getReimbursementApprovalFlowLabel() {
  return [
    getReimbursementStageName(REIMBURSEMENT_STATUSES.PENDING_L1),
    getReimbursementStageName(REIMBURSEMENT_STATUSES.PENDING_L2),
    getReimbursementStageName(REIMBURSEMENT_STATUSES.PENDING_FINAL),
  ].filter(Boolean).join(' → ')
}

/**
 * Approval-trail heading for one stored stage.
 * HR Pending | HR Approved | HR Rejected
 */
export function formatReimbursementTrailLabel(row, stageStatus) {
  if (!row) return formatReimbursementStageOutcome(stageStatus, 'pending')
  const status = normalizeReimbursementStatus(row)
  const rejectedStage = pendingStatusFromStageToken(row.rejected_stage || row.rejectedStage)
  const pending = pendingStatusFromStageToken(stageStatus)

  if (status === REIMBURSEMENT_STATUSES.REJECTED && rejectedStage === pending) {
    return formatReimbursementStageOutcome(pending, 'rejected')
  }

  const approvedByKey = pending === REIMBURSEMENT_STATUSES.PENDING_L1
    ? 'level1_approved_by'
    : pending === REIMBURSEMENT_STATUSES.PENDING_L2
      ? 'level2_approved_by'
      : 'final_approved_by'
  if (row[approvedByKey]) {
    return formatReimbursementStageOutcome(pending, 'approved')
  }

  return formatReimbursementStageOutcome(pending, 'pending')
}

/**
 * Default abilities mapping for each approver type.
 * Backend should use this as reference to generate abilities.
 */

export const ROLE_ABILITIES_MAP = {

  employee: [

    { action: 'read', subject: 'reimbursement' },

    { action: 'create', subject: 'reimbursement' },

  ],

  level1Approver: [

    { action: 'read', subject: 'reimbursement' },

    { action: 'create', subject: 'reimbursement' },

    { action: 'read', subject: REIMBURSEMENT_PERMISSIONS.VIEW_APPROVALS },

    { action: 'approve', subject: REIMBURSEMENT_PERMISSIONS.APPROVE_LEVEL1 },

  ],

  level2Approver: [

    { action: 'read', subject: 'reimbursement' },

    { action: 'create', subject: 'reimbursement' },

    { action: 'read', subject: REIMBURSEMENT_PERMISSIONS.VIEW_APPROVALS },

    { action: 'approve', subject: REIMBURSEMENT_PERMISSIONS.APPROVE_LEVEL2 },

  ],

  finalApprover: [

    { action: 'read', subject: 'reimbursement' },

    { action: 'create', subject: 'reimbursement' },

    { action: 'read', subject: REIMBURSEMENT_PERMISSIONS.VIEW_APPROVALS },

    { action: 'read', subject: REIMBURSEMENT_PERMISSIONS.VIEW_ALL },

    { action: 'download', subject: REIMBURSEMENT_PERMISSIONS.VIEW_ALL },

    { action: 'approve', subject: REIMBURSEMENT_PERMISSIONS.APPROVE_FINAL },

  ],

}



function tryParseUserData() {

  try {

    return JSON.parse(localStorage.getItem('userData')) || {}

  } catch (e) {

    return {}

  }

}



/**

 * @param {Function} canFn - The $can function from Vue component

 * @param {string} action - read, create, approve, apply_on_behalf

 * @param {string} subject - CASL subject

 */

export function hasPermission(canFn, action, subject) {

  if (typeof canFn !== 'function') return false

  return canFn(action, subject)

}



function resolveUser(user) {

  return user || tryParseUserData()

}



function hasAnyApprovalPermission(canFn) {

  return hasPermission(canFn, 'approve', REIMBURSEMENT_PERMISSIONS.APPROVE_LEVEL1)

    || hasPermission(canFn, 'approve', REIMBURSEMENT_PERMISSIONS.APPROVE_LEVEL2)

    || hasPermission(canFn, 'approve', REIMBURSEMENT_PERMISSIONS.APPROVE_FINAL)

}



/** @param {Function} canFn */

export function isFinalApprover(canFn) {

  return hasPermission(canFn, 'approve', REIMBURSEMENT_PERMISSIONS.APPROVE_FINAL)

}



function getPendingStatusesForUser(canFn) {

  const statuses = []

  if (hasPermission(canFn, 'approve', REIMBURSEMENT_PERMISSIONS.APPROVE_LEVEL1)) {

    statuses.push(REIMBURSEMENT_STATUSES.PENDING_L1)

  }

  if (hasPermission(canFn, 'approve', REIMBURSEMENT_PERMISSIONS.APPROVE_LEVEL2)) {

    statuses.push(REIMBURSEMENT_STATUSES.PENDING_L2)

  }

  if (hasPermission(canFn, 'approve', REIMBURSEMENT_PERMISSIONS.APPROVE_FINAL)) {

    statuses.push(REIMBURSEMENT_STATUSES.PENDING_FINAL)

  }

  return statuses

}



function resolveCanApproveStage(canFn, status) {

  const permission = PERMISSION_BY_STATUS[status]

  if (!permission) return false

  return hasPermission(canFn, 'approve', permission)

}



export function normalizeReimbursementStatus(claim) {

  if (!claim) return REIMBURSEMENT_STATUSES.PENDING_L1

  if (claim.status) return claim.status

  if (claim.is_approved === true || claim.is_approved === 'true') {

    return REIMBURSEMENT_STATUSES.APPROVED

  }

  return REIMBURSEMENT_STATUSES.PENDING_L1

}



function parseAddedBy(claim) {

  if (!claim || !claim.added_by) return {}

  if (typeof claim.added_by === 'object') return claim.added_by

  try {

    return JSON.parse(claim.added_by)

  } catch (e) {

    return {}

  }

}



function parsePersonValue(value) {

  if (value == null || value === '') return null

  if (typeof value === 'number') return { empId: value, title: '' }

  if (typeof value === 'string') {

    const trimmed = value.trim()

    if (!trimmed) return null

    try {

      return parsePersonValue(JSON.parse(trimmed))

    } catch (e) {

      if (/^\d+$/.test(trimmed)) return { empId: trimmed, title: '' }

      return { title: trimmed, empId: null }

    }

  }

  if (typeof value !== 'object') return null

  const name = `${value.first_name || value.firstName || ''} ${value.last_name || value.lastName || ''}`.trim()

  const title = value.title || value.name || value.employee_name || value.employeeName || value.emp_name || name || ''

  const empId = value.empId ?? value.emp_id ?? value.employee_id ?? value.applied_for ?? value.id ?? null

  if (!title && empId == null) return null

  return { title, empId, email: value.email || value.employee_email || null }

}

const ON_BEHALF_CACHE_KEY = 'reimb_on_behalf_v1'

export function rememberReimbursementOnBehalf(claimId, person) {
  if (claimId == null || claimId === '' || !person || !person.title) return
  try {
    const map = JSON.parse(sessionStorage.getItem(ON_BEHALF_CACHE_KEY) || '{}')
    map[String(claimId)] = {
      title: String(person.title).trim(),
      empId: person.empId != null ? person.empId : null,
      email: person.email || null,
    }
    sessionStorage.setItem(ON_BEHALF_CACHE_KEY, JSON.stringify(map))
  } catch (e) { /* ignore */ }
}

export function getRememberedReimbursementOnBehalf(claimId) {
  if (claimId == null || claimId === '') return null
  try {
    const map = JSON.parse(sessionStorage.getItem(ON_BEHALF_CACHE_KEY) || '{}')
    const row = map[String(claimId)]
    return row && row.title ? row : null
  } catch (e) {
    return null
  }
}

/** Beneficiary name on a person-shaped object — prefer on-behalf fields over raiser `title`. */
function employeeTitleFromObject(value) {
  if (!value || typeof value !== 'object') return ''
  const name = `${value.first_name || value.firstName || ''} ${value.last_name || value.lastName || ''}`.trim()
  return value.employee_name
    || value.employeeName
    || value.on_behalf_of
    || value.on_behalf
    || value.beneficiary_name
    || value.emp_name
    || name
    || value.title
    || value.name
    || ''
}



function personEmpId(person) {

  if (!person) return null

  return person.empId ?? person.emp_id ?? null

}



function asNamedPerson(value) {

  const parsed = parsePersonValue(value)

  if (!parsed) return null

  if (!parsed.title && personEmpId(parsed) == null) return null

  return parsed

}



/**

 * Logged-in actor who raised the claim. Prefer nested `submitted_by` / `raised_by`

 * so `added_by.title` is never mistaken for the beneficiary.

 */

export function getReimbursementSubmitterPerson(claim) {

  if (!claim) return null

  const addedBy = parseAddedBy(claim)

  const nested = asNamedPerson(

    addedBy.submitted_by

    || addedBy.raised_by

    || claim.submitted_by

    || claim.raised_by

  )

  if (nested && nested.title) return nested

  if (addedBy.title || addedBy.name || personEmpId(addedBy) != null) {

    return {

      title: addedBy.title || addedBy.name || '',

      empId: personEmpId(addedBy),

      email: addedBy.email || null,

    }

  }

  return nested

}



/**

 * Person who will receive the reimbursement (on-behalf employee, or self).

 */

export function getReimbursementEmployeePerson(claim) {

  if (!claim) return null

  const remembered = getRememberedReimbursementOnBehalf(claim.id ?? claim.claim_id)
  if (remembered && remembered.title) {
    return { title: remembered.title, empId: remembered.empId, email: null }
  }

  const addedBy = parseAddedBy(claim)

  const submitter = getReimbursementSubmitterPerson(claim)

  const submitterEmpId = personEmpId(submitter)

  const submitterTitle = (submitter && submitter.title) ? String(submitter.title).trim() : ''

  // Explicit beneficiary fields only — never `added_by.title` / decorated `employee` string.
  const namedCandidates = [

    addedBy.on_behalf_of,

    addedBy.on_behalf,

    addedBy.employee_name,

    addedBy.employeeName,

    addedBy.beneficiary_name,

    typeof addedBy.employee === 'string' ? addedBy.employee : null,

    claim.on_behalf_of,

    claim.onBehalfOf,

    claim.on_behalf,

    claim.employee_name,

    claim.employeeName,

    claim.emp_name,

    claim.beneficiary_name,

    claim.beneficiaryName,

  ]

  const idCandidates = [

    addedBy.applied_for,

    addedBy.appliedFor,

    addedBy.employee_id,

    addedBy.employeeId,

    claim.applied_for,

    claim.appliedFor,

    claim.employee_id,

    claim.employeeId,

    claim.beneficiary_id,

  ]

  let empId = null

  for (let i = 0; i < idCandidates.length; i += 1) {

    const parsed = parsePersonValue(idCandidates[i])

    const id = personEmpId(parsed)

    if (id != null && id !== '') {

      empId = id

      break

    }

    if (idCandidates[i] != null && idCandidates[i] !== '' && (typeof idCandidates[i] === 'string' || typeof idCandidates[i] === 'number')) {

      empId = idCandidates[i]

      break

    }

  }

  for (let i = 0; i < namedCandidates.length; i += 1) {

    const parsed = parsePersonValue(namedCandidates[i])

    if (parsed && parsed.title) {

      return {

        title: parsed.title,

        empId: personEmpId(parsed) != null ? personEmpId(parsed) : empId,

        email: parsed.email || addedBy.employee_email || null,

      }

    }

  }

  const objectCandidates = [

    addedBy.employee,

    addedBy.beneficiary,

    claim.beneficiary,

  ]

  for (let i = 0; i < objectCandidates.length; i += 1) {

    const value = objectCandidates[i]

    if (!value || typeof value !== 'object') continue

    const title = employeeTitleFromObject(value)

    const id = personEmpId(parsePersonValue(value))

    if (id != null && empId == null) empId = id

    if (title) {

      return {

        title,

        empId: id != null ? id : empId,

        email: value.email || value.employee_email || addedBy.employee_email || null,

      }

    }

  }

  // Decorated list rows set `employee` to the display string. Ignore it when it
  // merely repeats the raiser — that was the old "both columns show me" fallback.
  const employeeField = claim.employee

  if (typeof employeeField === 'string' && employeeField.trim() && employeeField.trim() !== submitterTitle) {

    return {

      title: employeeField.trim(),

      empId,

      email: addedBy.employee_email || null,

    }

  }

  if (employeeField && typeof employeeField === 'object') {

    const title = employeeTitleFromObject(employeeField)

    const id = personEmpId(parsePersonValue(employeeField))

    if (title && (!submitterTitle || title !== submitterTitle || (id != null && submitterEmpId != null && String(id) !== String(submitterEmpId)))) {

      return {

        title,

        empId: id != null ? id : empId,

        email: employeeField.email || addedBy.employee_email || null,

      }

    }

  }

  const addedByEmpId = personEmpId(addedBy)

  if (

    submitterEmpId != null

    && addedByEmpId != null

    && String(submitterEmpId) !== String(addedByEmpId)

    && (addedBy.title || addedBy.name)

  ) {

    return {

      title: addedBy.title || addedBy.name,

      empId: addedByEmpId,

      email: addedBy.email || null,

    }

  }

  if (empId != null && (submitterEmpId == null || String(empId) !== String(submitterEmpId))) {

    return { title: '', empId, email: addedBy.employee_email || null }

  }

  return submitter

}



/**

 * Person a claim was filed for (apply-on-behalf), when distinct from the submitter.

 */

export function getReimbursementOnBehalfPerson(claim) {

  const employee = getReimbursementEmployeePerson(claim)

  const submitter = getReimbursementSubmitterPerson(claim)

  if (!employee || !employee.title) return null

  const employeeEmpId = personEmpId(employee)

  const submitterEmpId = personEmpId(submitter)

  if (employeeEmpId != null && submitterEmpId != null && String(employeeEmpId) === String(submitterEmpId)) {

    return null

  }

  if (submitter && submitter.title && employee.title === submitter.title) return null

  return employee

}



export function getReimbursementSubmitterName(claim) {

  const person = getReimbursementSubmitterPerson(claim)

  return (person && person.title) || ''

}



export function getReimbursementEmployeeName(claim) {

  const person = getReimbursementEmployeePerson(claim)

  return (person && person.title) || ''

}



export function getReimbursementOnBehalfName(claim) {

  const person = getReimbursementOnBehalfPerson(claim)

  return (person && person.title) || ''

}



export function isOwnSubmission(user, claim) {

  if (!user || !claim) return false

  const addedBy = parseAddedBy(claim)

  const userEmpId = user.emp_id ?? user.empId

  if (userEmpId === undefined || userEmpId === null) return false

  const submitter = getReimbursementSubmitterPerson(claim)

  const submitterEmpId = personEmpId(submitter) ?? addedBy.empId ?? addedBy.emp_id

  if (submitterEmpId != null && String(submitterEmpId) === String(userEmpId)) return true

  const addedByEmpId = addedBy.empId ?? addedBy.emp_id

  if (addedByEmpId != null && String(addedByEmpId) === String(userEmpId)) return true

  const employee = getReimbursementEmployeePerson(claim)

  if (employee && personEmpId(employee) != null && String(personEmpId(employee)) === String(userEmpId)) return true

  const onBehalf = getReimbursementOnBehalfPerson(claim)

  if (onBehalf && onBehalf.empId != null && String(onBehalf.empId) === String(userEmpId)) return true

  const employeeId = claim.employee_id ?? claim.employeeId

  if (employeeId != null && String(employeeId) === String(userEmpId)) return true

  return false

}



export function isPendingReimbursement(claim) {

  const status = normalizeReimbursementStatus(claim)

  return PENDING_REIMBURSEMENT_STATUSES.includes(status)

}



function hasPrivilegedReimbursementEditAccess(canFn) {

  return hasAnyApprovalPermission(canFn)

    || canViewAllReimbursements(canFn)

}



/**

 * Employee may edit their own claim only while pending Level 1 (before L1 approval).

 * @param {Object} user

 * @param {Object} claim

 */

export function canEditOwnReimbursement(user, claim) {

  if (!claim || !isOwnSubmission(user, claim)) return false

  const status = normalizeReimbursementStatus(claim)

  return status === REIMBURSEMENT_STATUSES.PENDING_L1

}



/**

 * Manager / admin / HR may edit any claim at any status; employees only own + Pending Level 1.

 * @param {Function} canFn

 * @param {Object} user

 * @param {Object} claim

 */

export function canEditReimbursement(canFn, user, claim) {

  if (!claim) return false

  if (hasPrivilegedReimbursementEditAccess(canFn)) return true

  return canEditOwnReimbursement(user, claim)

}



/**

 * Employee may withdraw (delete) their own claim only while still pending approval.

 * @param {Object} user

 * @param {Object} claim

 */

export function canCancelOwnReimbursement(user, claim) {

  if (!claim || !isOwnSubmission(user, claim)) return false

  return isPendingReimbursement(claim)

}



export function getEditDenialReason(canFn, user, claim) {

  if (!claim) return 'No claim selected.'

  if (canEditReimbursement(canFn, user, claim)) return null

  const data = resolveUser(user)

  if (!isOwnSubmission(data, claim) && !hasPrivilegedReimbursementEditAccess(canFn)) {

    return 'You can only edit your own reimbursements.'

  }

  const status = normalizeReimbursementStatus(claim)

  if (status === REIMBURSEMENT_STATUSES.APPROVED) {

    return 'Approved reimbursements cannot be edited.'

  }

  if (isOwnSubmission(data, claim) && status !== REIMBURSEMENT_STATUSES.PENDING_L1) {

    return `You can only edit your reimbursement while it is ${formatReimbursementStageOutcome(REIMBURSEMENT_STATUSES.PENDING_L1, 'pending')}.`

  }

  return 'This reimbursement cannot be edited in its current state.'

}



export function getCancelDenialReason(user, claim) {

  if (!claim) return 'No claim selected.'

  if (!isOwnSubmission(user, claim)) return 'You can only withdraw your own reimbursements.'

  const status = normalizeReimbursementStatus(claim)

  if (status === REIMBURSEMENT_STATUSES.APPROVED) {

    return 'Approved reimbursements cannot be withdrawn.'

  }

  if (status === REIMBURSEMENT_STATUSES.REJECTED) {

    return 'Rejected reimbursements cannot be withdrawn. Edit and resubmit instead.'

  }

  if (!isPendingReimbursement(claim)) {

    return 'Only pending reimbursements can be withdrawn.'

  }

  return null

}



/** @param {Function} canFn */

export function canViewApprovalsTab(canFn) {

  return hasPermission(canFn, 'read', REIMBURSEMENT_PERMISSIONS.VIEW_APPROVALS)

    || hasAnyApprovalPermission(canFn)

}



/** @param {Function} canFn */

export function canViewAllReimbursements(canFn) {

  return hasPermission(canFn, 'read', REIMBURSEMENT_PERMISSIONS.VIEW_ALL)

}



/**

 * Finance / final approvers may export reimbursement data (CSV).

 * Prefers explicit `download reimbursement_all`; falls back to `read reimbursement_all`.

 * @param {Function} canFn

 */

export function canDownloadReimbursements(canFn) {

  return hasPermission(canFn, 'download', REIMBURSEMENT_PERMISSIONS.VIEW_ALL)

    || hasPermission(canFn, 'read', REIMBURSEMENT_PERMISSIONS.VIEW_ALL)

}



/**

 * @param {Function} canFn

 * @param {Object} claim

 * @param {Object} [user] - for own-submission check only

 */

export function canApproveAtStage(canFn, claim, user) {

  const data = resolveUser(user)

  if (!claim) return false

  if (isOwnSubmission(data, claim)) return false



  const status = normalizeReimbursementStatus(claim)

  if (status === REIMBURSEMENT_STATUSES.APPROVED || status === REIMBURSEMENT_STATUSES.REJECTED) {

    return false

  }



  return resolveCanApproveStage(canFn, status)

}



/**

 * Human-readable reason when approve is blocked (for toasts / guards).

 * @returns {string|null} null when approval is allowed

 */

export function getApproveDenialReason(canFn, claim, user) {

  const data = resolveUser(user)

  if (!claim) return 'No claim selected.'

  if (isOwnSubmission(data, claim)) return 'Self-approval is not allowed.'



  const status = normalizeReimbursementStatus(claim)

  if (status === REIMBURSEMENT_STATUSES.APPROVED || status === REIMBURSEMENT_STATUSES.REJECTED) {

    return 'Claim is not pending approval.'

  }



  if (resolveCanApproveStage(canFn, status)) return null



  const required = PERMISSION_BY_STATUS[status]

  return `You are not authorized to act at this approval stage. Required: approve ${required}.`

}



/** @param {Function} canFn */

export function filterApprovalQueue(canFn, claims, user) {

  if (!Array.isArray(claims)) return []

  const data = resolveUser(user)

  const pendingStatuses = getPendingStatusesForUser(canFn)



  return claims.filter(claim => {

    if (isOwnSubmission(data, claim)) return false

    const status = normalizeReimbursementStatus(claim)

    return pendingStatuses.includes(status)

  })

}



export function deriveDisplayStatus(statusOrRow) {

  if (typeof statusOrRow === 'string') return statusOrRow

  return normalizeReimbursementStatus(statusOrRow)

}



export function deriveDisplayStatusVariant(statusOrRow) {

  const status = deriveDisplayStatus(statusOrRow)

  switch (status) {

    case REIMBURSEMENT_STATUSES.APPROVED:

      return 'success'

    case REIMBURSEMENT_STATUSES.REJECTED:

      return 'danger'

    case REIMBURSEMENT_STATUSES.PENDING_L1:

    case REIMBURSEMENT_STATUSES.PENDING_L2:

    case REIMBURSEMENT_STATUSES.PENDING_FINAL:

      return 'warning'

    default:

      return 'secondary'

  }

}



export function buildApproverSnapshot(user) {

  const data = user || tryParseUserData()

  const name = `${data.first_name || ''} ${data.last_name || ''}`.trim()

  return {

    title: name || data.title || 'Unknown',

    empId: data.emp_id ?? data.empId,

    email: data.email || null,

  }

}



/**

 * Claim-owner snapshot for `added_by` (self or apply-on-behalf employee).

 * Same shape the create API already accepts: `{ title, empId, email }`.

 */

export function toAddedBySnapshot(person) {

  if (!person) return null

  const name = `${person.first_name || person.firstName || ''} ${person.last_name || person.lastName || ''}`.trim()

  const empId = person.emp_id ?? person.empId ?? person.employee_id ?? person.employeeId ?? person.id

  const title = name || person.title || person.name || (empId != null ? String(empId) : '')

  if (empId == null && !title) return null

  return {

    title: title || 'Unknown',

    empId,

    email: person.email || null,

  }

}



/**

 * Show "Apply on behalf" only for the dedicated reimbursement CASL subjects.

 * Leave keys (apply_on_behalf_leave, apply_on_behalf_leave_all, leave_all)

 * do not unlock reimbursement. Team-dashboard view is not enough.

 */

export function canApplyReimbursementOnBehalf(canFn) {

  return hasPermission(canFn, 'apply_on_behalf', REIMBURSEMENT_PERMISSIONS.APPLY_ON_BEHALF)

    || hasPermission(canFn, 'apply_on_behalf', REIMBURSEMENT_PERMISSIONS.APPLY_ON_BEHALF_ALL)

}



/** HR-Director / Admin / Master: pick any active employee, not only reportees. */

export function canApplyReimbursementOnBehalfAll(canFn) {

  return hasPermission(canFn, 'apply_on_behalf', REIMBURSEMENT_PERMISSIONS.APPLY_ON_BEHALF_ALL)

}



export default {

  REIMBURSEMENT_PERMISSIONS,

  PERMISSION_ACTIONS,

  ROLE_ABILITIES_MAP,

  REIMBURSEMENT_STATUSES,

  STATUS_FOR_APPROVAL_PERMISSION,

  PERMISSION_BY_STATUS,

  hasPermission,

  normalizeReimbursementStatus,

  isOwnSubmission,

  getReimbursementOnBehalfPerson,

  getReimbursementSubmitterPerson,

  getReimbursementEmployeePerson,

  getReimbursementSubmitterName,

  getReimbursementEmployeeName,

  getReimbursementOnBehalfName,

  PENDING_REIMBURSEMENT_STATUSES,

  isPendingReimbursement,

  canEditOwnReimbursement,

  canEditReimbursement,

  canCancelOwnReimbursement,

  getEditDenialReason,

  getCancelDenialReason,

  isFinalApprover,

  canViewApprovalsTab,

  canViewAllReimbursements,

  canDownloadReimbursements,

  canApproveAtStage,

  getApproveDenialReason,

  filterApprovalQueue,

  REIMBURSEMENT_STAGE_ROLE_NAMES,

  getReimbursementStageName,

  formatReimbursementStageOutcome,

  formatReimbursementStatusLabel,

  getReimbursementApprovalFlowLabel,

  formatReimbursementTrailLabel,

  deriveDisplayStatus,

  deriveDisplayStatusVariant,

  buildApproverSnapshot,

  toAddedBySnapshot,

  canApplyReimbursementOnBehalf,

  canApplyReimbursementOnBehalfAll,

}


